import { useMemo, useState, useEffect, useCallback } from 'react';
// @mui
import Collapse from '@mui/material/Collapse';
// routes
import { usePathname } from '@/routes/hooks';
import { useActiveLink } from '@/routes/hooks/use-active-link';
//
import { NavListProps, NavConfigProps } from '../types';
import NavItem from './nav-item';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
  config: NavConfigProps;
};

export default function NavList({ data, depth, hasChild, config }: NavListRootProps) {
  const pathname = usePathname();

  const active = useActiveLink(data.path, hasChild);

  const externalLink = useMemo(() => data.path.includes('http'), [data.path]);

  const [open, setOpen] = useState(active);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!active) {
      handleClose();
    }
  }, [pathname, active, handleClose]);

  const navItemProps = useMemo(
    () => ({
      item: data,
      depth,
      open,
      active,
      externalLink,
      onClick: handleToggle,
      config,
    }),
    [data, depth, open, active, externalLink, handleToggle, config]
  );

  return (
    <>
      <NavItem {...navItemProps} />

      {hasChild && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} config={config} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
  config: NavConfigProps;
};

function NavSubList({ data, depth, config }: NavListSubProps) {
  const renderSubItems = useMemo(
    () =>
      data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          config={config}
        />
      )),
    [data, depth, config]
  );

  return <>{renderSubItems}</>;
}
